import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, of, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectHasValidSubscription, selectSubscriptionLoading } from '../../state/subscription/subscription.selectors';
import { take, switchMap } from 'rxjs/operators';

@Injectable()
export class SubscriptionInterceptorService implements HttpInterceptor {
  // URLs that should be allowed even without a valid subscription
  private excludedUrls: string[] = [
    '/auth', // Authentication endpoints
    '/api/stripe/subscription/status', // Subscription status check
    '/api/stripe/subscription/setup', // Subscription setup
    '/api/stripe/publishable-key', // Stripe publishable key
    '/api/stripe/customer-session', // Customer session for pricing table
    '/api/stripe/customer-portal-session', // Customer portal session
    '/api/stripe/webhook', // Stripe webhook (server-to-server)
    '/subscription', // Subscription page routes (frontend)
    '/profile', // Profile page routes (frontend)
  ];

  constructor(private store: Store) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Check if this URL should be excluded from subscription check
    const shouldExclude = this.excludedUrls.some((url) => req.url.includes(url));

    if (shouldExclude) {
      // Allow the request to proceed
      return next.handle(req);
    }

    // Check subscription status - combine both loading and valid subscription state
    return combineLatest([
      this.store.select(selectSubscriptionLoading).pipe(take(1)),
      this.store.select(selectHasValidSubscription).pipe(take(1)),
    ]).pipe(
      switchMap(([loading, hasValidSubscription]) => {
        if (loading) {
          // If subscription is still loading, allow the request
          // (we don't want to block requests while checking subscription)
          return next.handle(req);
        }

        if (!hasValidSubscription) {
          // Block the request if subscription is not valid
          console.warn('[Subscription Interceptor] Blocking request due to invalid subscription:', req.url);
          // Return an empty observable to cancel the request
          return of();
        }

        // Allow the request if subscription is valid
        return next.handle(req);
      })
    );
  }
}
